(() => {
  const initialise = (page) => {
    if (!page || page.dataset.passwordReady) return;
    page.dataset.passwordReady = 'true';
    const root = document.documentElement;
    const controller = new AbortController();
    const listen = (node, event, handler) => node?.addEventListener(event, handler, { signal: controller.signal });
    const strings = JSON.parse(page.querySelector('[data-password-strings]')?.textContent || '{}');
    const countdown = page.querySelector('[data-password-countdown]');
    const gameButton = page.querySelector('[data-password-lightshow]');
    const confetti = page.querySelector('[data-password-confetti]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dialog = page.querySelector('#password-disco-game');
    const closeButton = page.querySelector('[data-password-game-close]');
    const scoreNode = page.querySelector('[data-password-score]');
    const statusNode = page.querySelector('[data-password-game-status]');
    const tiles = [...page.querySelectorAll('[data-password-tile]')];
    const audio = page.querySelector('[data-password-audio]');
    const musicButtons = [...page.querySelectorAll('[data-password-music]')];
    const musicStatus = page.querySelector('[data-password-music-status]');
    const videos = [...page.querySelectorAll('video')];
    let timer;
    let confettiTimer;
    let score = 0;
    let target = -1;
    let wantsMusic = false;
    let musicRequest = 0;
    let keyBuffer = '';
    let previousFocus;
    const GAME_GOAL = 5;
    const GAME_DURATION = 20;
    const TARGET_VISIBLE_STAGES = reducedMotion ? [900, 820, 740, 660, 580] : [720, 650, 580, 510, 440];
    const TARGET_INTERVAL_STAGES = reducedMotion ? [1250, 1140, 1030, 920, 810] : [1050, 930, 820, 720, 640];
    const gameTimerNode = page.querySelector('[data-password-game-timer]');
    const levelNode = page.querySelector('[data-password-level]');
    const resetButton = page.querySelector('[data-password-game-reset]');
    let secondsLeft = GAME_DURATION;
    let gameActive = false;
    let gameCountdownTimer;
    let gameTargetTimer;
    let gameHideTimer;

    const updateCountdown = () => {
      if (!countdown) return;
      const targetTime = new Date(countdown.dataset.target).getTime();
      if (!Number.isFinite(targetTime)) { countdown.hidden = true; return; }
      const remaining = Math.max(0, targetTime - Date.now());
      const values = { days: Math.floor(remaining / 86400000), hours: Math.floor(remaining / 3600000) % 24, minutes: Math.floor(remaining / 60000) % 60, seconds: Math.floor(remaining / 1000) % 60 };
      Object.entries(values).forEach(([name, value]) => {
        const node = countdown.querySelector(`[data-${name}]`);
        if (node) node.textContent = String(value).padStart(2, '0');
      });
      if (!remaining) window.clearInterval(timer);
    };

    const updateMusic = () => {
      const playing = Boolean(audio && !audio.paused);
      page.classList.toggle('is-music-playing', playing);
      musicButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(playing));
        const label = button.querySelector('[data-music-label]');
        if (label) label.textContent = playing ? strings.musicOff : strings.musicOn;
      });
    };
    const musicKey = 'shop-sante-disco-music';
    const getMusicPreference = () => {
      try { return window.localStorage.getItem(musicKey) || window.sessionStorage.getItem(musicKey); }
      catch (_) { try { return window.sessionStorage.getItem(musicKey); } catch (_) { return null; } }
    };
    const saveMusicPreference = (value) => {
      try { window.sessionStorage.setItem(musicKey, value); } catch (_) {}
      try { window.localStorage.setItem(musicKey, value); } catch (_) {}
    };
    let lastMusicSave = 0;
    const saveMusicPosition = () => {
      if (!audio?.readyState) return;
      try { window.sessionStorage.setItem('shop-sante-disco-music-time', String(audio.currentTime)); } catch (_) {}
    };
    const pauseMusic = (remember = true) => {
      musicRequest++;
      wantsMusic = false;
      if (remember) saveMusicPreference('manual-paused');
      saveMusicPosition();
      audio?.pause();
      updateMusic();
    };
    const playMusic = async (remember = true) => {
      if (!audio) return;
      const request = ++musicRequest;
      wantsMusic = true;
      if (remember) saveMusicPreference('playing');
      videos.forEach((video) => video.pause());
      audio.volume = 0.55;
      if (musicStatus) musicStatus.textContent = '';
      try {
        await audio.play();
        if (request !== musicRequest) return;
        if (!wantsMusic || getMusicPreference() === 'manual-paused') audio.pause();
        updateMusic();
      } catch (_) {
        if (request !== musicRequest) return;
        if (wantsMusic && musicStatus) musicStatus.textContent = strings.musicError;
        wantsMusic = false;
        updateMusic();
      }
    };
    const toggleMusic = () => wantsMusic ? pauseMusic() : playMusic();
    listen(window, 'storage', (event) => {
      if (event.key === musicKey && event.newValue !== 'playing') pauseMusic(false);
    });
    listen(audio, 'loadedmetadata', () => {
      let savedTime = 0;
      try { savedTime = Number(window.sessionStorage.getItem('shop-sante-disco-music-time')); } catch (_) {}
      if (Number.isFinite(savedTime) && savedTime > 0 && savedTime < audio.duration - 2) audio.currentTime = savedTime;
    });
    listen(audio, 'timeupdate', () => {
      if (Date.now() - lastMusicSave > 2000) { saveMusicPosition(); lastMusicSave = Date.now(); }
    });
    listen(window, 'pageshow', (event) => {
      if (event.persisted && getMusicPreference() === 'playing') playMusic(false);
    });
    musicButtons.forEach((button) => listen(button, 'click', toggleMusic));
    listen(audio, 'play', () => { if (!wantsMusic || getMusicPreference() === 'manual-paused') audio.pause(); updateMusic(); });
    listen(audio, 'pause', updateMusic);
    listen(audio, 'error', () => {
      const requested = wantsMusic;
      pauseMusic();
      if (requested && musicStatus) musicStatus.textContent = strings.musicError;
    });
    videos.forEach((video) => listen(video, 'play', () => {
      const musicWasPlaying = wantsMusic;
      pauseMusic();
      videos.forEach((other) => { if (other !== video) other.pause(); });
      if (musicWasPlaying && musicStatus) musicStatus.textContent = strings.musicVideo;
    }));
    videos.forEach((video) => {
      const playButton = video.closest('.password-disco__reel-player')?.querySelector('[data-password-video-play]');
      if (!playButton) return;
      const updateVideoButton = () => { playButton.hidden = !video.paused && !video.ended; };
      updateVideoButton();
      listen(playButton, 'click', async () => {
        playButton.hidden = true;
        try { await video.play(); } catch (_) { updateVideoButton(); }
      });
      listen(video, 'play', updateVideoButton);
      listen(video, 'pause', updateVideoButton);
      listen(video, 'ended', updateVideoButton);
    });
    const reelGrid = page.querySelector('.password-disco__reel-grid');
    const reelPrevious = page.querySelector('[data-password-reel-prev]');
    const reelNext = page.querySelector('[data-password-reel-next]');
    const updateReelNavigation = () => {
      if (!reelGrid || !reelPrevious || !reelNext) return;
      reelPrevious.disabled = reelGrid.scrollLeft < 5;
      reelNext.disabled = reelGrid.scrollLeft + reelGrid.clientWidth >= reelGrid.scrollWidth - 5;
    };
    const moveReel = (direction) => {
      const first = reelGrid?.firstElementChild;
      if (!first) return;
      const step = first.getBoundingClientRect().width + parseFloat(getComputedStyle(reelGrid).gap || '0');
      reelGrid.scrollBy({ left: direction * step, behavior: reducedMotion ? 'auto' : 'smooth' });
    };
    listen(reelPrevious, 'click', () => moveReel(-1));
    listen(reelNext, 'click', () => moveReel(1));
    listen(reelGrid, 'scroll', updateReelNavigation);
    listen(window, 'resize', updateReelNavigation);
    updateReelNavigation();
    listen(window, 'pagehide', () => { pauseMusic(false); stopGame(); });

    // Match the storefront DJ booth: 5 targets, 20 seconds, accelerating flashes.
    const setTarget = (visible = false) => {
      tiles.forEach((tile, index) => {
        const active = visible && index === target;
        tile.classList.toggle('is-target', active);
        tile.dataset.baseLabel ||= tile.getAttribute('aria-label');
        tile.setAttribute('aria-label', active ? `${tile.dataset.baseLabel}, ${strings.target}` : tile.dataset.baseLabel);
      });
    };
    const clearGameTimers = () => {
      window.clearInterval(gameCountdownTimer);
      window.clearTimeout(gameTargetTimer);
      window.clearTimeout(gameHideTimer);
    };
    const updateGameDisplay = () => {
      scoreNode.textContent = `${score} / ${GAME_GOAL}`;
      gameTimerNode.textContent = `${secondsLeft} s`;
      levelNode.textContent = `${strings.level} ${Math.min(score + 1, GAME_GOAL)} / ${GAME_GOAL}`;
    };
    const stopGame = () => {
      clearGameTimers(); gameActive = false; target = -1; setTarget(false);
    };
    const resetGame = () => {
      stopGame(); score = 0; secondsLeft = GAME_DURATION;
      dialog?.classList.remove('is-won');
      root.dataset.discoParty = 'off';
      statusNode.textContent = strings.gameReady;
      resetButton.textContent = strings.start;
      updateGameDisplay();
    };
    const finishGame = (won) => {
      stopGame(); resetButton.textContent = strings.replay;
      statusNode.textContent = won ? `${strings.gameWin} ${secondsLeft} s.` : `${strings.gameLost} ${score} / ${GAME_GOAL}.`;
      if (won) celebrate();
    };
    const showNextTarget = () => {
      if (!gameActive || !tiles.length) return;
      window.clearTimeout(gameTargetTimer); window.clearTimeout(gameHideTimer);
      setTarget(false);
      const offset = 1 + Math.floor(Math.random() * Math.max(1, tiles.length - 1));
      target = target < 0 ? Math.floor(Math.random() * tiles.length) : (target + offset) % tiles.length;
      setTarget(true);
      const stage = Math.min(score, GAME_GOAL - 1);
      gameHideTimer = window.setTimeout(() => setTarget(false), TARGET_VISIBLE_STAGES[stage]);
      gameTargetTimer = window.setTimeout(showNextTarget, TARGET_INTERVAL_STAGES[stage]);
    };
    const startGame = () => {
      resetGame(); gameActive = true;
      resetButton.textContent = strings.restart;
      statusNode.textContent = strings.gameGo;
      showNextTarget();
      gameCountdownTimer = window.setInterval(() => {
        secondsLeft = Math.max(0, secondsLeft - 1);
        updateGameDisplay();
        if (secondsLeft === 0) finishGame(false);
      }, 1000);
    };
    const celebrate = () => {
      dialog?.classList.add('is-won');
      root.dataset.discoParty = 'on';
      if (reducedMotion || !confetti) return;
      window.clearTimeout(confettiTimer);
      confetti.replaceChildren();
      for (let i = 0; i < 34; i += 1) {
        const piece = document.createElement('i');
        piece.style.left = `${Math.random() * 100}%`;
        piece.style.animationDelay = `${Math.random() * .6}s`;
        piece.style.setProperty('--drift', `${-90 + Math.random() * 180}px`);
        piece.style.setProperty('--spin', `${180 + Math.random() * 720}deg`);
        confetti.append(piece);
      }
      confettiTimer = window.setTimeout(() => confetti.replaceChildren(), 2800);
    };
    tiles.forEach((tile) => listen(tile, 'click', () => {
      if (!gameActive) { statusNode.textContent = strings.gameReady; return; }
      if (Number(tile.dataset.passwordTile) === target && tile.classList.contains('is-target')) {
        score += 1;
        updateGameDisplay();
        if (score >= GAME_GOAL) { finishGame(true); return; }
        statusNode.textContent = `${strings.gameHit} ${GAME_GOAL - score}.`;
        showNextTarget();
      } else {
        secondsLeft = Math.max(0, secondsLeft - 1);
        updateGameDisplay();
        statusNode.textContent = strings.gameMiss;
        if (secondsLeft === 0) finishGame(false);
      }
    }));
    const openGame = () => {
      if (!dialog || dialog.open) return;
      previousFocus = document.activeElement;
      resetGame();
      dialog.showModal();
      root.classList.add('password-game-open');
      closeButton.focus({ preventScroll: true });
    };
    listen(gameButton, 'click', openGame);
    listen(closeButton, 'click', () => dialog.close());
    listen(dialog, 'close', () => {
      stopGame();
      root.classList.remove('password-game-open');
      root.dataset.discoParty = 'off';
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    });
    listen(dialog, 'click', (event) => {
      const box = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) dialog.close();
    });
    listen(resetButton, 'click', startGame);
    listen(page.querySelector('[data-password-scroll-promos]'), 'click', () => {
      page.querySelector('#password-disco-promos')?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    });
    page.querySelectorAll('[data-password-video]').forEach((wrapper) => {
      listen(wrapper.querySelector('button'), 'click', () => {
        const id = wrapper.dataset.videoId;
        const provider = wrapper.dataset.videoProvider;
        if (!id || !['youtube', 'vimeo'].includes(provider)) return;
        pauseMusic();
        videos.forEach((video) => video.pause());
        const iframe = document.createElement('iframe');
        iframe.title = 'Vente Futée Disco';
        iframe.loading = 'lazy';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.src = provider === 'youtube' ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0` : `https://player.vimeo.com/video/${encodeURIComponent(id)}?autoplay=1&dnt=1`;
        wrapper.replaceChildren(iframe);
      });
    });
    listen(document, 'keydown', (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1 || event.target.closest('input, textarea, select, [contenteditable]')) return;
      keyBuffer = `${keyBuffer}${event.key.toLowerCase()}`.slice(-5);
      if (keyBuffer === 'disco') { keyBuffer = ''; openGame(); }
    });
    listen(document, 'shopify:section:unload', (event) => {
      if (!event.target.contains(page)) return;
      pauseMusic(false);
      if (dialog?.open) dialog.close();
      stopGame();
      window.clearInterval(timer);
      window.clearTimeout(confettiTimer);
      controller.abort();
    });
    timer = window.setInterval(updateCountdown, 1000);
    updateCountdown();
    updateMusic();
    if (getMusicPreference() === 'playing') playMusic(false);
  };
  document.querySelectorAll('[data-password-disco]').forEach(initialise);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-password-disco]').forEach(initialise));
})();
